import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import ConsentSection from '../components/ConsentSection';
import { toast } from 'sonner';
import {
  User,
  ArrowLeft,
  Mail,
  Phone,
  Home,
  Building2,
  Shield,
  Edit2,
  Save,
  LogOut,
  Bell,
  BellOff,
  Moon,
  ChevronRight,
  Loader2,
  Send,
  AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
  });
  
  // Push notifications - simple state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [browserPermission, setBrowserPermission] = useState('default');
  
  // Check browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setBrowserPermission(Notification.permission);
    }
  }, []);

  const handleEnableNotifications = async () => {
    console.log('[Notifications] Enable button clicked');
    
    if (!('Notification' in window)) {
      toast.error('Notifications not supported in this browser');
      return;
    }

    // Check current permission
    const currentPermission = Notification.permission;
    console.log('[Notifications] Current permission:', currentPermission);
    
    if (currentPermission === 'denied') {
      toast.error('Notifications are blocked. Please enable them in your browser settings.', {
        duration: 5000,
        description: 'Click the lock icon in your address bar to change notification settings.'
      });
      return;
    }

    setNotificationsLoading(true);
    setBrowserPermission(currentPermission);
    
    try {
      console.log('[Notifications] Requesting permission...');
      
      // If already granted, skip permission request
      let permission = currentPermission;
      if (currentPermission !== 'granted') {
        permission = await Notification.requestPermission();
      }
      
      console.log('[Notifications] Permission result:', permission);
      setBrowserPermission(permission);
      
      if (permission === 'granted') {
        console.log('[Notifications] Registering with backend...');
        
        // Get session token
        const token = localStorage.getItem('session_token');
        
        // Register with backend
        const response = await fetch(`${API_URL}/api/push/register-token`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            token: 'web-push-' + Date.now(),
            device_type: 'web'
          })
        });

        console.log('[Notifications] Backend response:', response.status);
        
        if (response.ok) {
          setNotificationsEnabled(true);
          toast.success('Push notifications enabled!');
          // Show a test notification
          new Notification('BodyCorp365', {
            body: 'Notifications are now enabled!',
            icon: '/logo192.png'
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('[Notifications] Backend error:', errorData);
          toast.error(errorData.detail || 'Failed to register for notifications');
        }
      } else if (permission === 'denied') {
        toast.error('Notifications blocked by browser', {
          duration: 5000,
          description: 'Click the lock icon in your address bar to allow notifications.'
        });
      } else {
        toast.error('Notification permission was not granted');
      }
    } catch (error) {
      console.error('[Notifications] Error:', error);
      toast.error('Failed to enable notifications');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${API_URL}/api/push/unregister-token`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setNotificationsEnabled(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      toast.error('Failed to disable notifications');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: 'Test Notification',
          body: 'Push notifications are working!'
        })
      });
      
      if (response.ok) {
        toast.success('Test notification sent!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test');
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(formData);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      resident: 'bg-sky-100 text-sky-700',
      trustee: 'bg-purple-100 text-purple-700',
      property_manager: 'bg-emerald-100 text-emerald-700',
      security: 'bg-amber-100 text-amber-700',
      platform_owner: 'bg-slate-800 text-white',
    };
    return colors[role] || colors.resident;
  };

  const getBackPath = () => {
    if (user?.role === 'trustee' || user?.role === 'property_manager') return '/admin';
    if (user?.role === 'managing_agent' || user?.role === 'agent_admin') return '/agent';
    if (user?.role === 'platform_owner') return '/platform';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={getBackPath()}>
              <Button variant="ghost" size="icon" data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Profile</h1>
          </div>
          {!editing ? (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="edit-profile-btn"
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          ) : (
            <Button 
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </>
              )}
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Profile Card */}
        <Card data-testid="profile-card">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center">
              <Avatar className="w-24 h-24 mb-4">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-sky-100 text-sky-700 text-2xl">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              {editing ? (
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="text-center text-xl font-bold mb-2"
                  data-testid="name-input"
                />
              ) : (
                <h2 className="text-xl font-bold font-manrope text-slate-900">{user?.name || 'User'}</h2>
              )}
              <Badge className={`mt-2 ${getRoleBadgeColor(user?.role)}`}>
                {user?.role?.replace('_', ' ') || 'Resident'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card data-testid="contact-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-manrope">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium text-slate-900">{user?.email || 'Not set'}</p>
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-500">Phone</p>
                {editing ? (
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Enter phone number"
                    className="mt-1"
                    data-testid="phone-input"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{user?.phone || 'Not set'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unit Info */}
        <Card data-testid="unit-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-manrope">My Unit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
                <Home className="w-5 h-5 text-sky-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-500">Unit Number</p>
                <p className="font-medium text-slate-900">{user?.unit_id || 'Not assigned'}</p>
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-sky-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-500">Community</p>
                <p className="font-medium text-slate-900">{user?.hoa_id || 'Demo Community'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <ConsentSection />
        <Card data-testid="settings-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-manrope">Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {/* Browser Permission Warning */}
            {browserPermission === 'denied' && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Notifications Blocked</p>
                  <p className="text-amber-700 mt-1">
                    Click the lock/info icon in your browser's address bar, find "Notifications", and change to "Allow".
                  </p>
                </div>
              </div>
            )}
            
            {/* Push Notifications Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3">
                {notificationsEnabled ? (
                  <Bell className="w-5 h-5 text-emerald-600" />
                ) : (
                  <BellOff className="w-5 h-5 text-slate-400" />
                )}
                <div>
                  <span className="font-medium text-slate-900">Push Notifications</span>
                  <p className="text-xs text-slate-500">
                    {notificationsEnabled 
                      ? 'Enabled - You\'ll receive alerts' 
                      : browserPermission === 'denied' 
                        ? 'Blocked by browser' 
                        : 'Disabled - Click to enable'}
                  </p>
                </div>
              </div>
              <Button
                variant={notificationsEnabled ? "default" : "outline"}
                size="sm"
                onClick={notificationsEnabled ? handleDisableNotifications : handleEnableNotifications}
                disabled={notificationsLoading}
                className={notificationsEnabled ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                data-testid="notifications-toggle"
              >
                {notificationsLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : notificationsEnabled ? (
                  'On'
                ) : (
                  'Enable'
                )}
              </Button>
            </div>
            {/* Test Notification Button */}
            {notificationsEnabled && (
              <Button 
                variant="outline"
                onClick={handleTestNotification}
                className="w-full justify-start"
                data-testid="test-notification-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Test Notification
              </Button>
            )}
            <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3">
                <Moon className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-900">Dark Mode</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
            <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-900">Security</span>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          </CardContent>
        </Card>

        {/* Logout */}
        <Button 
          variant="outline" 
          className="w-full h-12 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-full"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </Button>
      </main>
    </div>
  );
}
